import React, { useState } from "react";
import {
  Form,
  Space,
  Modal,
  Upload,
  Button,
  Table,
  message,
  Typography,
} from "antd";
import { UploadOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";
import { apiRequest } from "../../../../service/apiRequest";

const { Text } = Typography;

interface Props {
  visible: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

/* -------------------------------------------------------------
   LEDGER COLUMNS — CLEANED + MATCHES YOUR BACKEND COLUMN FLOW
-------------------------------------------------------------- */
const ledgerKeys = [
  "ledger_account_head",
  "ledger_sub_account_head",
  "ledger_first_name",
  "ledger_last_name",
  "contact_first_name",
  "ledger_company_name",
  "address_detail",
  "address_landmark",
  "address_city",
  "address_state",
  "address_country",
  "address_type",
  "ledger_email",
  "contact_type_landline",
  "ledger_company_mobile",
  "contact_type_mobile",
  "address_pincode",
  "link_name_website",
  "ledger_marriage_anniversary",
  "ledger_dob",
  "ledger_occupation",
  "ledger_designation",
  "ledger_deals_in_product",
  "ledger_deals_in_company",
  "link_url_instagram",
  "ledger_gstin",
  "ledger_pan_no",
  "ledger_notes",
  "address_name_company",
  "address_city_name_company",
  "ledger_msme_reg_no",
  "address_county_name_company",
  "address_landmark_name_company",
  "address_pincode_company",
  "ledger_tin_no",
];

/* -------------------------------------------------------------
   SAMPLE TEMPLATE — AUTO-GENERATED EMPTY ROW
-------------------------------------------------------------- */
const sampleTemplate = [Object.fromEntries(ledgerKeys.map((key) => [key, ""]))];

const ImportLedgerModal: React.FC<Props> = ({
  visible,
  onClose,
  onSuccess,
}) => {
  const [parsedData, setParsedData] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);

  /* -------------------------------------------------------------
     DOWNLOAD SAMPLE TEMPLATE
  -------------------------------------------------------------- */
  const downloadSample = () => {
    const ws = XLSX.utils.json_to_sheet(sampleTemplate);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "LedgerTemplate");
    XLSX.writeFile(wb, "ledger_import_template.xlsx");
  };

  /* -------------------------------------------------------------
     UPLOAD EXCEL FILE
  -------------------------------------------------------------- */
  const handleUpload = async (file: File) => {
    setLoading(true);
    try {
      const buffer = await file.arrayBuffer();
      const workbook = XLSX.read(buffer, { type: "array" });
      const sheet = workbook.Sheets[workbook.SheetNames[0]];
      const json = XLSX.utils.sheet_to_json(sheet, { defval: "" });

      if (!json.length) {
        message.warning("Excel file is empty.");
        return false;
      }

      setParsedData(json);
    } catch (error) {
      console.error(error);
      message.error("Failed to read Excel file. Please check format.");
    } finally {
      setLoading(false);
    }

    return false;
  };

  /* -------------------------------------------------------------
     SUBMIT IMPORT
  -------------------------------------------------------------- */
  const handleImport = async () => {
    if (!parsedData.length) {
      return message.error("No data to import.");
    }

    try {
      await apiRequest({
        method: "post",
        url: "/import-ledgers",
        data: parsedData,
      });

      message.success("Ledgers imported successfully!");
      onSuccess();
      onClose();
    } catch (error) {
      console.error(error);
      message.error("Import failed. Check data formatting.");
    }
  };

  return (
    <Modal
      title="Import Ledger"
      width="90%"
      open={visible}
      onCancel={onClose}
      onOk={handleImport}
      okText="Import"
      okButtonProps={{ disabled: parsedData.length === 0, loading }}
      destroyOnClose
    >
      <Form layout="vertical">
        {/* STEP 1 – DOWNLOAD TEMPLATE */}
        <Form.Item label="Step 1">
          <Space direction="vertical" size={4}>
            <Text type="secondary">
              Download the sample template and fill it with ledger data.
            </Text>
            <Button icon={<DownloadOutlined />} onClick={downloadSample}>
              Download Sample Template
            </Button>
          </Space>
        </Form.Item>

        {/* STEP 2 – UPLOAD FILE */}
        <Form.Item label="Step 2">
          <Space direction="vertical" size={4}>
            <Text type="secondary">Upload your filled Excel file below.</Text>
            <Upload
              accept=".xlsx"
              beforeUpload={handleUpload}
              showUploadList={false}
              disabled={loading}
            >
              <Button icon={<UploadOutlined />} loading={loading}>
                Upload Excel File
              </Button>
            </Upload>
          </Space>
        </Form.Item>
      </Form>

      {/* PREVIEW TABLE */}
      {parsedData.length > 0 && (
        <>
          <Text style={{ marginTop: 12, display: "block" }}>Preview:</Text>
          <Table
            dataSource={parsedData}
            rowKey={(_, index) => index!.toString()}
            size="small"
            scroll={{ y: 300, x: "max-content" }}
            pagination={false}
            columns={Object.keys(parsedData[0] || {}).map((key) => ({
              title: key,
              dataIndex: key,
              key,
            }))}
            style={{ marginTop: 8 }}
          />
        </>
      )}
    </Modal>
  );
};

export default ImportLedgerModal;
